"use client"

import React, { useEffect, useMemo, useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { KeyRound, Loader2, Plus, RefreshCw } from "lucide-react"
import { Blade, PillButton } from "@/components/blades/blade"
import { HubCard, StatusPill, ToolHeader, ToolbarPill, hubTh, hubTr } from "@/components/hub/hub-canvas"
import { toast } from "sonner"
import { useAtsSources, type AtsSourceInfo } from "@/hooks/use-ats-sources"
import { invalidateReferenceDataCache } from "@/hooks/use-explorer"
import { getApiErrorDetail } from "@/lib/api"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import { useCode } from "@/contexts/code-context"

// The one statement of switch consequences — shown wherever a switch can happen
// so the guidance can't drift between the inline note and the confirmation dialog
const LINKED_ENTITIES_WARNING_KEY = "atsSources.switch.linkedEntitiesWarning"

/**
 * Admin management of the tenant's ATS sources: list configured sources, create
 * new ones (credentials live separately in the integration service's Key Vault),
 * and select the active source when several exist. Entities already linked to a
 * source (work orders/assignments with ATS placement IDs, permanent placements,
 * client organization mappings) keep their original source binding.
 */
export function AtsSourcesPage() {
  const { t } = useTranslation()
  const { loadSources, loadAtsSystems, createSource, activateSource, reconnectAtsSystem } = useAtsSources()
  // Hooks like useAtsInfo still resolve source → system from the client code-value
  // snapshot; refresh it after source changes so they don't diverge until a reload
  const { refreshCodeValues } = useCode()

  const [sources, setSources] = useState<AtsSourceInfo[]>([])
  const [atsSystems, setAtsSystems] = useState<AtsSourceInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create form
  const [newCode, setNewCode] = useState("")
  const [newName, setNewName] = useState("")
  const [newDescription, setNewDescription] = useState("")
  const [newSystemId, setNewSystemId] = useState<string>("")
  const [makeActive, setMakeActive] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)

  // A dismissed form must not come back half-filled: a stale code or a ticked "make active" is a wrong source waiting to be created
  const closeCreate = () => {
    setCreateOpen(false)
    setNewCode("")
    setNewName("")
    setNewDescription("")
    setNewSystemId("")
    setMakeActive(false)
  }

  // Switch confirmation
  const [pendingActivation, setPendingActivation] = useState<AtsSourceInfo | null>(null)
  const [isActivating, setIsActivating] = useState(false)

  // Reconnect form
  const [reconnectOpen, setReconnectOpen] = useState(false)
  const [reconnectSystem, setReconnectSystem] = useState("")
  const [isReconnecting, setIsReconnecting] = useState(false)

  const refresh = async () => {
    try {
      setSources(await loadSources())
      setError(null)
    } catch (err) {
      setError(getApiErrorDetail(err) ?? (err instanceof Error ? err.message : t("atsSources.messages.loadFailed")))
    }
  }

  useEffect(() => {
    Promise.all([loadSources(), loadAtsSystems()])
      .then(([loadedSources, systems]) => {
        setSources(loadedSources)
        setAtsSystems(systems)
      })
      .catch((err) =>
        setError(getApiErrorDetail(err) ?? (err instanceof Error ? err.message : t("atsSources.messages.loadFailed")))
      )
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activeSource = useMemo(() => sources.find((s) => s.isActive) ?? null, [sources])

  // Credentials belong to the tenant per ATS system, so sources on the same system reconnect together
  const reconnectableSystems = useMemo(
    () => [...new Set(sources.map((s) => s.atsSystem).filter((system): system is string => !!system))],
    [sources]
  )

  const openReconnect = () => {
    setReconnectSystem(activeSource?.atsSystem ?? reconnectableSystems[0] ?? "")
    setReconnectOpen(true)
  }

  const handleCreate = async () => {
    if (!newCode.trim() || !newSystemId) {
      toast.error(t("atsSources.messages.codeAndSystemRequired"))
      return
    }

    setIsCreating(true)
    try {
      await createSource({
        code: newCode.trim(),
        name: newName.trim() || newCode.trim(),
        description: newDescription.trim() || undefined,
        atsSystemId: Number(newSystemId),
        makeActive: sources.length === 0 ? true : makeActive,
      })
      toast.success(t("atsSources.messages.created", { code: newCode.trim() }))
      setNewCode("")
      setNewName("")
      setNewDescription("")
      setNewSystemId("")
      setMakeActive(false)
      await refresh()
      invalidateReferenceDataCache()
      refreshCodeValues().catch(() => undefined)
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? (err instanceof Error ? err.message : t("atsSources.messages.createFailed")))
    } finally {
      setIsCreating(false)
    }
  }

  const handleActivate = async () => {
    if (!pendingActivation) return
    setIsActivating(true)
    try {
      await activateSource(pendingActivation.code)
      toast.success(t("atsSources.messages.activated", { code: pendingActivation.code }))
      setPendingActivation(null)
      await refresh()
      // Reference data was resolved against the previous source's credentials
      invalidateReferenceDataCache()
      refreshCodeValues().catch(() => undefined)
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? (err instanceof Error ? err.message : t("atsSources.messages.activateFailed")))
    } finally {
      setIsActivating(false)
    }
  }

  const handleReconnect = async () => {
    if (!reconnectSystem) return
    setIsReconnecting(true)
    try {
      await reconnectAtsSystem(reconnectSystem)
      toast.success(t("atsSources.messages.reconnected", { system: reconnectSystem }))
      setReconnectOpen(false)
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? t("atsSources.messages.reconnectFailed", { system: reconnectSystem }))
    } finally {
      setIsReconnecting(false)
    }
  }

  const canCreate = !isCreating && newCode.trim() !== "" && newSystemId !== ""

  return (
    <div className="p-4">
      <ToolHeader
        title={t("atsSources.header.title")}
        description={
          sources.length === 0
            ? t("atsSources.header.descriptionEmpty")
            : activeSource
              ? t("atsSources.header.descriptionActive", { code: activeSource.code })
              : t("atsSources.header.descriptionMultiple")
        }
        actions={
          <>
            {reconnectableSystems.length > 0 && (
              <ToolbarPill onClick={openReconnect} icon={<RefreshCw className="h-3.5 w-3.5" />}>
                {t("atsSources.reconnect.action")}
              </ToolbarPill>
            )}
            <ToolbarPill variant="primary" onClick={() => setCreateOpen(true)} icon={<Plus className="h-3.5 w-3.5" />}>
              {t("atsSources.header.newSource")}
            </ToolbarPill>
          </>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <HubCard>
        {isLoading ? (
          <div className="flex items-center px-4 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("common.state.loading")}
          </div>
        ) : sources.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className={hubTh}>{t("atsSources.table.code")}</TableHead>
                <TableHead className={hubTh}>{t("atsSources.table.name")}</TableHead>
                <TableHead className={hubTh}>{t("atsSources.table.atsSystem")}</TableHead>
                <TableHead className={hubTh}>{t("atsSources.table.description")}</TableHead>
                <TableHead className={`${hubTh} w-36 text-right`}>{t("atsSources.table.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sources.map((source) => (
                <TableRow key={source.id} className={hubTr}>
                  <TableCell className="font-mono text-xs font-semibold">{source.code}</TableCell>
                  <TableCell className="text-[13px]">{source.name}</TableCell>
                  <TableCell className="text-[13px]">{source.atsSystem ?? "—"}</TableCell>
                  <TableCell className="max-w-64 truncate text-xs text-muted-foreground">{source.description}</TableCell>
                  <TableCell className="text-right">
                    {source.isActive ? (
                      <StatusPill tone="active">{t("atsSources.table.active")}</StatusPill>
                    ) : (
                      <ToolbarPill className="h-7 px-2.5 text-xs" onClick={() => setPendingActivation(source)}>
                        {t("atsSources.table.makeActive")}
                      </ToolbarPill>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t("atsSources.table.empty")}</p>
        )}
      </HubCard>

      <Blade
        open={createOpen}
        onClose={closeCreate}
        size="md"
        eyebrow={t("atsSources.create.eyebrow")}
        title={t("atsSources.create.title")}
        description={t("atsSources.create.description")}
        footer={
          <>
            <PillButton variant="ghost" onClick={closeCreate}>{t("common.actions.cancel")}</PillButton>
            <PillButton onClick={handleCreate} disabled={!canCreate}>
              {isCreating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} {t("atsSources.create.submit")}
            </PillButton>
          </>
        }
      >
        <div className="space-y-4">
          <Alert>
            <KeyRound className="h-4 w-4" />
            <AlertTitle>{t("atsSources.credentials.title")}</AlertTitle>
            <AlertDescription>{t("atsSources.credentials.description")}</AlertDescription>
          </Alert>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="sourceCode">{t("atsSources.create.sourceCode")}</Label>
              <Input
                id="sourceCode"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                placeholder={t("atsSources.create.sourceCodePlaceholder")}
              />
              <p className="text-xs text-muted-foreground">{t("atsSources.create.sourceCodeHelp")}</p>
            </div>
            <div className="space-y-1.5">
              <Label>{t("atsSources.create.atsSystem")}</Label>
              <Select value={newSystemId} onValueChange={setNewSystemId}>
                <SelectTrigger>
                  <SelectValue placeholder={t("atsSources.create.atsSystemPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {atsSystems.map((system) => (
                    <SelectItem key={system.id} value={String(system.id)}>
                      {system.name || system.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="sourceName">{t("atsSources.create.displayName")}</Label>
              <Input id="sourceName" value={newName} onChange={(e) => setNewName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sourceDescription">{t("atsSources.create.descriptionField")}</Label>
              <Input
                id="sourceDescription"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </div>
          </div>

          {sources.length > 0 && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={makeActive} onCheckedChange={(checked) => setMakeActive(checked === true)} />
                {t("atsSources.create.makeActive")}
              </label>
              {makeActive && <p className="text-xs text-amber-600">{t(LINKED_ENTITIES_WARNING_KEY)}</p>}
            </div>
          )}

        </div>
      </Blade>

      <AlertDialog open={pendingActivation !== null} onOpenChange={(open) => !open && setPendingActivation(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("atsSources.switch.title", { code: pendingActivation?.code ?? "" })}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  {t("atsSources.switch.bindingsPrefix")}
                  <strong>{pendingActivation?.code}</strong>
                  {t("atsSources.switch.bindingsSuffix")}
                </p>
                <p className="text-amber-600">{t(LINKED_ENTITIES_WARNING_KEY)}</p>
                <p>
                  {t("atsSources.switch.syncPrefix")}
                  <strong>{pendingActivation?.code}</strong>
                  {t("atsSources.switch.syncSuffix")}
                </p>
                <p>{t("atsSources.switch.credentials")}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleActivate} disabled={isActivating}>
              {isActivating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t("atsSources.switch.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Blade
        open={reconnectOpen}
        onClose={() => setReconnectOpen(false)}
        size="md"
        eyebrow={t("atsSources.reconnect.eyebrow")}
        title={t("atsSources.reconnect.title")}
        description={t("atsSources.reconnect.description")}
        footer={
          <>
            <PillButton variant="ghost" onClick={() => setReconnectOpen(false)}>{t("common.actions.cancel")}</PillButton>
            <PillButton onClick={handleReconnect} disabled={isReconnecting || !reconnectSystem}>
              {isReconnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}{" "}
              {t("atsSources.reconnect.submit")}
            </PillButton>
          </>
        }
      >
        <div className="space-y-4">
          <Alert>
            <RefreshCw className="h-4 w-4" />
            <AlertTitle>{t("atsSources.reconnect.whenTitle")}</AlertTitle>
            <AlertDescription>{t("atsSources.reconnect.whenDescription")}</AlertDescription>
          </Alert>

          <div className="space-y-1.5">
            <Label>{t("atsSources.reconnect.atsSystem")}</Label>
            <Select value={reconnectSystem} onValueChange={setReconnectSystem}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reconnectableSystems.map((system) => (
                  <SelectItem key={system} value={system}>
                    {system}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Blade>
    </div>
  )
}
